// หนังสือแจ้งลาออก + แบบสอบถามก่อนพ้นสภาพ. Pure (no I/O).
//
// Why this exists at all: people stop coming to work and go quiet, so the clinic
// never learns why and the same cause recurs. The feature is worth exactly as much
// as the honesty of the answers, which is why the questionnaire is short, and why
// every string here says the answers reach ฝ่ายบุคคล and nobody else.
//
// ⚠️ CONFIDENTIAL, NOT ANONYMOUS. The answers are tied to the person, and must be —
// HR cannot act on feedback from nobody. Never write "ไม่ระบุชื่อ" anywhere in this
// feature. If true anonymity is ever wanted, that is a different thing (token
// submitted, no FK) and must not be bolted onto this one.

// Statutory/contractual notice. Defaulted into the form and freely editable, with
// NO warning if they pick something shorter — the owner's call: the clinic would
// rather hear about a short notice than not hear at all.
export const NOTICE_DAYS = 30

export const RESIGN_STATUS = {
  pending: 'รอพิจารณา',
  accepted: 'รับทราบแล้ว',
  withdrawn: 'ถอนแล้ว',
}

export const RESIGN_TONE = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  accepted: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  withdrawn: 'bg-slate-100 text-slate-500 ring-slate-200',
}

export const resignLabel = (s) => RESIGN_STATUS[s] || s || '—'
export const resignTone = (s) => RESIGN_TONE[s] || RESIGN_TONE.withdrawn

// Today + NOTICE_DAYS in Bangkok, as 'YYYY-MM-DD'. The form's default last day.
export function defaultLastDay(now = Date.now()) {
  return new Date(now + 7 * 3600 * 1000 + NOTICE_DAYS * 86400000).toISOString().slice(0, 10)
}

export function bkkToday(now = Date.now()) {
  return new Date(now + 7 * 3600 * 1000).toISOString().slice(0, 10)
}

// Whole days of notice actually given. Shown to HR as information, never as a
// judgement on the employee's screen.
export function noticeGiven(filedAt, lastDay) {
  const a = Number(filedAt) || Date.parse(filedAt)
  const b = Date.parse(`${lastDay}T00:00:00+07:00`)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return Math.round((b - a) / 86400000)
}

/**
 * 🚨 The label for `resignation.last_working_date`.
 *
 * Employee.date_end is the column payroll and the roster actually read, and
 * accepting a resignation deliberately does NOT write it — a human saves it in the
 * employee editor. Until that happens the two can disagree, so this one is called
 * "วันสุดท้ายที่ขอ" and only becomes "วันสุดท้าย" once `applied_to_employee` is true.
 * Two unlabelled answers to "when did they leave" is the failure this prevents.
 */
export const lastDayLabel = (r) => (r?.applied_to_employee ? 'วันทำงานวันสุดท้าย' : 'วันสุดท้ายที่ขอ')

export const CONFIDENTIAL_TH =
  'คำตอบทั้งหมดในแบบสอบถามนี้ ฝ่ายบุคคลเท่านั้นที่เห็นได้ — หัวหน้างานและผู้จัดการสาขาจะไม่เห็นคำตอบของท่าน ' +
  'ตอบตามจริงได้เต็มที่ ข้อมูลนี้จะถูกใช้เพื่อปรับปรุงการดูแลพนักงานเท่านั้น'

export const BEFORE_SUBMIT_TH =
  'เมื่อส่งแล้ว ฝ่ายบุคคลหรือผู้จัดการจะติดต่อกลับเร็ว ๆ นี้'

/**
 * The off-boarding questionnaire.
 *
 * `key` is a canonical token and is NEVER the Thai text — the same rule
 * evaluation_form.questions keeps, so rewording a question does not orphan every
 * answer already stored against it. The whole definition is frozen into
 * resignation_survey.answers at submit, so editing this list later cannot rewrite
 * what somebody actually said.
 *
 * Kept deliberately short. The person most likely to ghost is exactly the person
 * who will not open a long form; the win here is for the ones who WOULD tell you.
 */
export const OFFBOARD_QUESTIONS = [
  {
    key: 'reasons',
    type: 'multi',
    label: 'เหตุผลหลักที่ตัดสินใจลาออก (เลือกได้มากกว่า 1 ข้อ)',
    options: [
      'ค่าตอบแทน', 'โอกาสเติบโตในงาน', 'หัวหน้างาน', 'เพื่อนร่วมงาน',
      'ภาระงานหนักเกินไป', 'เวลาทำงาน / ตารางเวร', 'สุขภาพ', 'เรื่องครอบครัว',
      'ย้ายที่อยู่', 'เรียนต่อ', 'ได้งานใหม่', 'อื่นๆ',
    ],
  },
  { key: 'rate_supervisor', type: 'scale', label: 'ความพึงพอใจต่อหัวหน้างานโดยตรง', min: 1, max: 5 },
  { key: 'rate_pay', type: 'scale', label: 'ความพึงพอใจต่อค่าตอบแทนและสวัสดิการ', min: 1, max: 5 },
  { key: 'rate_team', type: 'scale', label: 'ความพึงพอใจต่อบรรยากาศและเพื่อนร่วมงาน', min: 1, max: 5 },
  { key: 'rate_workload', type: 'scale', label: 'ความพึงพอใจต่อภาระงานและตารางเวร', min: 1, max: 5 },
  {
    key: 'training',
    type: 'choice',
    label: 'ได้รับการอบรมและเครื่องมือเพียงพอต่อการทำงานหรือไม่',
    options: ['เพียงพอ', 'พอใช้', 'ไม่เพียงพอ'],
  },
  {
    key: 'raised_before',
    type: 'text',
    label: 'เคยแจ้งปัญหานี้กับใครก่อนหรือไม่ และผลเป็นอย่างไร',
    placeholder: 'เช่น เคยคุยกับหัวหน้าสาขาแล้ว แต่ยังไม่มีการเปลี่ยนแปลง',
  },
  { key: 'nps', type: 'scale', label: 'จะแนะนำให้เพื่อนมาทำงานที่นี่หรือไม่ (0 = ไม่แนะนำเลย, 10 = แนะนำอย่างยิ่ง)', min: 0, max: 10 },
  {
    key: 'return',
    type: 'choice',
    label: 'หากมีโอกาส จะกลับมาร่วมงานอีกหรือไม่',
    options: ['กลับมาแน่นอน', 'อาจจะ', 'ไม่กลับมา'],
  },
  { key: 'good', type: 'text', label: 'สิ่งที่คลินิกทำได้ดีแล้ว และอยากให้รักษาไว้', placeholder: '' },
  { key: 'improve', type: 'text', label: 'สิ่งที่คลินิกควรปรับปรุงมากที่สุด', placeholder: '' },
]

// Freeze the question text alongside the answer, so a later reword cannot change
// the meaning of what someone already submitted.
export function buildAnswers(values) {
  return OFFBOARD_QUESTIONS.map((q) => ({
    key: q.key,
    label: q.label,
    type: q.type,
    value: values?.[q.key] ?? (q.type === 'multi' ? [] : ''),
  }))
}

export function blankAnswers() {
  const out = {}
  for (const q of OFFBOARD_QUESTIONS) out[q.key] = q.type === 'multi' ? [] : ''
  return out
}

// Rehydrate the editor from a stored answers[] — matched on `key`, never position,
// so inserting a question does not shift every stored answer by one.
export function answersToValues(answers) {
  const out = blankAnswers()
  for (const a of Array.isArray(answers) ? answers : []) {
    if (a && a.key in out) out[a.key] = a.value
  }
  return out
}

// How much of it they filled in — HR reads a 3/11 differently from an 11/11.
export function answeredCount(values) {
  let n = 0
  for (const q of OFFBOARD_QUESTIONS) {
    const v = values?.[q.key]
    if (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '') n += 1
  }
  return n
}
